import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { useEffect, useMemo } from 'react'
import { useTrackChangesUpdate } from '../services/TrackChangesUpdate/hooks/useTrackChangesUpdate'
import { useEditorWithSidebar } from './useEditorWithSidebar'

export interface UseTrackChangesPanelEffectProps {
    isSuggestionsDataUpdated: boolean
    isTreeLoading: boolean
    elementId: string | undefined
    setIsLoadingCards: (loading: boolean) => void
    debouncedFetchAndProcess: () => void
    isSidebarVisible: boolean
}

/**
 * Hook to conditionally trigger loading and fetching of suggestion data.
 * Triggers the fetch function if:
 * - Suggestions were updated
 * - Tree is not loading
 * - A valid elementId is available
 *
 * @param {UseTrackChangesPanelEffectProps} props- Indicates if the suggestions data has been updated.
 * @returns {void}
 */
export const useTrackChangesPanelEffect = ({
    isSuggestionsDataUpdated,
    isTreeLoading,
    elementId,
    setIsLoadingCards,
    debouncedFetchAndProcess,
    isSidebarVisible,
}: UseTrackChangesPanelEffectProps): void => {
    const {
        trackChangesData,
        setIsSuggestionsDataUpdated,
    } = useSuggestionsShallowSelector((state) => ({
        trackChangesData: state.trackChangesData,
        setIsSuggestionsDataUpdated: state.setIsSuggestionsDataUpdated,
    }))
    const { updateTrackChanges, isServiceAvailable } = useTrackChangesUpdate()
    const { sidebarVisibilityRef } = useEditorWithSidebar()
    // Memoize the track changes data state to avoid expensive operations
    const hasTrackChangesData = useMemo(
        () =>
            Boolean(
                trackChangesData && Object.keys(trackChangesData).length > 0
            ),
        [trackChangesData]
    )
    const canFetchSuggestions =
        isSuggestionsDataUpdated &&
        !isTreeLoading &&
        Boolean(elementId) &&
        isSidebarVisible

    // Memoize the fetch condition to avoid recalculating on every render
    const shouldFetch = useMemo(
        () => canFetchSuggestions && !hasTrackChangesData,
        [canFetchSuggestions, hasTrackChangesData]
    )

    const shouldFetchOnSidebarVisible = useMemo(
        () =>
            canFetchSuggestions &&
            hasTrackChangesData &&
            isServiceAvailable &&
            sidebarVisibilityRef.current,
        [canFetchSuggestions, hasTrackChangesData, isServiceAvailable]
    )

    useEffect(() => {
        if (shouldFetch) {
            setIsLoadingCards(true)
            debouncedFetchAndProcess()
        }
    }, [shouldFetch, setIsLoadingCards, debouncedFetchAndProcess])

    useEffect(() => {
        if (shouldFetchOnSidebarVisible) {
            setIsSuggestionsDataUpdated(false)
            updateTrackChanges()
            sidebarVisibilityRef.current = false
        }
    }, [
        shouldFetchOnSidebarVisible,
        updateTrackChanges,
        setIsSuggestionsDataUpdated,
    ])
}
